const APP_TIMEZONE = "America/Toronto";

export const SESSION_BOUNDS_ERROR = Object.freeze({
  INVALID: "invalid",
  END_BEFORE_START: "end_before_start",
  START_IN_FUTURE: "start_in_future",
  END_IN_FUTURE: "end_in_future",
});

/** CrewPilot currently uses a strict now-check; keep a named hook for clock skew. */
export const SESSION_FUTURE_TOLERANCE_MS = 0;

export function formatDate(dateString) {
  if (!dateString) return "—";

  try {
    return new Date(dateString).toLocaleDateString(
      "en-CA",
      {
        timeZone: APP_TIMEZONE,
        month: "short",
        day: "numeric",
        year: "numeric",
      }
    );
  } catch {
    return dateString;
  }
}

export function formatShortDate(dateString) {
  if (!dateString) return "—";

  try {
    return new Date(dateString).toLocaleDateString(
      "en-CA",
      {
        timeZone: APP_TIMEZONE,
        month: "short",
        day: "numeric",
      }
    );
  } catch {
    return dateString;
  }
}

export function formatTime(dateString) {
  if (!dateString) return "—";

  try {
    return new Date(dateString).toLocaleTimeString(
      "en-CA",
      {
        timeZone: APP_TIMEZONE,
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  } catch {
    return "—";
  }
}

export function toLocalDateInputValue(dateInput) {
  if (!dateInput) {
    return "";
  }

  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function formatTimeInputValue(date) {
  if (!date || Number.isNaN(date.getTime())) {
    return "";
  }

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${hours}:${minutes}`;
}

export function combineLocalDateAndTime(dateIso, timeValue) {
  if (!dateIso || !timeValue) {
    return null;
  }

  const date = new Date(`${dateIso}T${timeValue}`);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
}

export function isSameLocalCalendarDate(startInput, endInput) {
  const startDate = toLocalDateInputValue(startInput);
  const endDate = toLocalDateInputValue(endInput);

  return Boolean(startDate) && startDate === endDate;
}

export function computeSessionDurationSeconds(start, end) {
  if (!start || !end) {
    return 0;
  }

  return Math.floor((end.getTime() - start.getTime()) / 1000);
}

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatCompactClock(date) {
  const hours24 = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const period = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;

  return `${hours12}:${minutes} ${period}`;
}

function formatCompactMonthDay(date) {
  return `${SHORT_MONTHS[date.getMonth()]} ${date.getDate()}`;
}

export function formatCompactDuration(hours) {
  const totalMinutes = Math.round(Number(hours || 0) * 60);
  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;

  if (hrs === 0) return `${mins}m`;
  if (mins === 0) return `${hrs}h`;

  return `${hrs}h ${mins}m`;
}

/**
 * Compact Time Logs / payroll range.
 * Same day: "Sep 14 · 8:00 AM – 4:30 PM · 8h 30m"
 * Overnight: "Sep 14, 10:00 PM → Sep 15, 2:00 AM · 4h"
 */
export function formatSessionRangeLabel(
  startInput,
  endInput,
  { hours = null } = {},
) {
  const start = startInput instanceof Date ? startInput : new Date(startInput);
  const end = endInput instanceof Date ? endInput : new Date(endInput);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "—";
  }

  const durationHours =
    hours == null
      ? computeSessionDurationSeconds(start, end) / 3600
      : Number(hours);
  const durationLabel = formatCompactDuration(durationHours);
  const startClock = formatCompactClock(start);
  const endClock = formatCompactClock(end);

  if (isSameLocalCalendarDate(start, end)) {
    return `${formatCompactMonthDay(start)} · ${startClock} – ${endClock} · ${durationLabel}`;
  }

  return `${formatCompactMonthDay(start)}, ${startClock} → ${formatCompactMonthDay(end)}, ${endClock} · ${durationLabel}`;
}

export function formatSessionShiftLabel(startInput, endInput) {
  if (!startInput || !endInput) {
    return "—";
  }

  const start = startInput instanceof Date ? startInput : new Date(startInput);
  const end = endInput instanceof Date ? endInput : new Date(endInput);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return "—";
  }

  const startClock = formatCompactClock(start);
  const endClock = formatCompactClock(end);

  if (isSameLocalCalendarDate(start, end)) {
    return `${startClock} – ${endClock}`;
  }

  return `${formatCompactMonthDay(start)}, ${startClock} → ${formatCompactMonthDay(end)}, ${endClock}`;
}

function isTimestampInFuture(date, now) {
  return date.getTime() > now.getTime() + SESSION_FUTURE_TOLERANCE_MS;
}

/**
 * Resolve manual / review session start/end from complete local timestamps.
 * On edit, preserves sub-minute precision when date+minute inputs match the loaded log.
 */
export function resolveManualTimelogBounds({
  dateIso,
  startDateIso,
  endDateIso,
  startTime,
  endTime,
  originalLog = null,
  now = null,
  rejectFuture = false,
}) {
  const resolvedStartDate = startDateIso || dateIso;
  const resolvedEndDate = endDateIso || dateIso;
  const start = combineLocalDateAndTime(resolvedStartDate, startTime);
  const end = combineLocalDateAndTime(resolvedEndDate, endTime);

  if (!start || !end) {
    return {
      start,
      end,
      isValid: false,
      error: SESSION_BOUNDS_ERROR.INVALID,
    };
  }

  const originalStart = originalLog?.start_time
    ? new Date(originalLog.start_time)
    : null;
  const originalEnd = originalLog?.end_time
    ? new Date(originalLog.end_time)
    : null;
  const hasOriginalBounds =
    originalStart &&
    originalEnd &&
    !Number.isNaN(originalStart.getTime()) &&
    !Number.isNaN(originalEnd.getTime()) &&
    originalEnd > originalStart;

  const sameStartMinute =
    Boolean(originalStart) &&
    startTime === formatTimeInputValue(originalStart);
  const sameEndMinute =
    Boolean(originalEnd) &&
    endTime === formatTimeInputValue(originalEnd);
  const sameStartDate =
    Boolean(originalStart) &&
    resolvedStartDate === toLocalDateInputValue(originalStart);
  const sameEndDate =
    Boolean(originalEnd) &&
    resolvedEndDate === toLocalDateInputValue(originalEnd);

  let resolvedStart = start;
  let resolvedEnd = end;

  if (
    hasOriginalBounds &&
    sameStartMinute &&
    sameEndMinute &&
    sameStartDate &&
    sameEndDate
  ) {
    resolvedStart = originalStart;
    resolvedEnd = originalEnd;
  } else if (end <= start) {
    return {
      start,
      end,
      isValid: false,
      error: SESSION_BOUNDS_ERROR.END_BEFORE_START,
    };
  }

  if (rejectFuture) {
    const referenceNow = now || new Date();

    if (isTimestampInFuture(resolvedStart, referenceNow)) {
      return {
        start: resolvedStart,
        end: resolvedEnd,
        isValid: false,
        error: SESSION_BOUNDS_ERROR.START_IN_FUTURE,
      };
    }

    if (isTimestampInFuture(resolvedEnd, referenceNow)) {
      return {
        start: resolvedStart,
        end: resolvedEnd,
        isValid: false,
        error: SESSION_BOUNDS_ERROR.END_IN_FUTURE,
      };
    }
  }

  return {
    start: resolvedStart,
    end: resolvedEnd,
    isValid: true,
    error: null,
  };
}

export function sessionBoundsAlertMessage(error) {
  if (error === SESSION_BOUNDS_ERROR.START_IN_FUTURE) {
    return "Start time cannot be in the future.";
  }

  if (error === SESSION_BOUNDS_ERROR.END_IN_FUTURE) {
    return "End time cannot be in the future.";
  }

  if (error === SESSION_BOUNDS_ERROR.END_BEFORE_START) {
    return "End time must be after start time.";
  }

  return "Date, start time, and end time are required.";
}

export function formatHoursMinutes(hours) {
  const totalMinutes = Math.round(
    Number(hours || 0) * 60
  );

  const hrs = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;

  if (hrs === 0) return `${mins} min`;
  if (mins === 0) return `${hrs} hr${hrs !== 1 ? "s" : ""}`;

  return `${hrs} hr${hrs !== 1 ? "s" : ""} ${mins} min`;
}
